import React, { useEffect, useState } from 'react';
import { Box, Card, Typography, Breadcrumbs, Divider, Button, Skeleton } from '@mui/material';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Formik, Form } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { useResponseHandler } from 'hooks/useResponseHandler';
import validationSchema from './formik/validationSchema';
import agentsFormModel from './formik/agentsFormModel';
import { AgentesForm } from './components/forms/AgentesForm';
import { editAgentStore, getAgentByIdStore, getDataFormDropDownStore } from 'store/agentes/thunks';
import { MessagesDrawerRight } from 'components/MessagesDrawerRight';
import formInitialValues from './formik/formInitialValues';
import { agentsMapper } from './mappers/agents.mapper';

export const EditAgentes = () => {
    const dispatch:any = useDispatch();
    const navigate = useNavigate();
    const { id } = useParams();
    const { handleResponse, ResponseComponents } = useResponseHandler();
    const { formId, formField } = agentsFormModel;
    const [dataErrors, setMsgErrors] = useState([]);
    
    const [initialValues, setInitialValues] = useState<any>(formInitialValues);
    const [loading, setLoading] = useState(true);

    const { form_options } = useSelector((state: any) => state.agentes);

    

    const servicesOptions = form_options?.services || [];

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        if (!id) return;
        setLoading(true);
        const response = await dispatch(getAgentByIdStore(id));
        if (response?.ok && response?.data) {
            const item = response.data;
            const mappedDomain = agentsMapper.toDomain(item);
            
            // Extract services from the response since the backend now returns them here
            const optionsFromResponse = item.services?.services || [];
            
            setInitialValues({
                name: mappedDomain.name,
                description: mappedDomain.description || '',
                service_id: optionsFromResponse.find((s: any) => s.value === mappedDomain.serviceId) || undefined,
                systemPrompt: mappedDomain.systemPrompt,
                temperature: mappedDomain.temperature ?? '',
                topP: mappedDomain.topP ?? '',
                maxTokens: mappedDomain.maxTokens ?? '',
                enableTools: mappedDomain.enableTools ?? false,
                enableMemory: mappedDomain.enableMemory ?? false,
                enableReasoning: mappedDomain.enableReasoning ?? false,
                enableFunctionCall: mappedDomain.enableFunctionCall ?? false,
                enableSearch: mappedDomain.enableSearch ?? false,
                enableCodeExecution: mappedDomain.enableCodeExecution ?? false,
                executionMode: mappedDomain.executionMode || 'AUTO'
            });
        } else {
            navigate('/inteligencia-artificial/agentes');
        }
        setLoading(false);
    };

    const handleSubmit = async (values: any, actions: any) => {
        if (!id) return;
        actions.setSubmitting(true);

        const dto = agentsMapper.toDto(values);
        const response = await dispatch(editAgentStore(id, dto));
        
        await handleResponse(response, 'Agente Actualizado Con Éxito', true);
        actions.setSubmitting(false);
    };

    if (loading) return <Box sx={{ p: 4 }}><Skeleton variant="rectangular" height={400} /></Box>;

    return (
        <Box sx={{ width: '100%' }}>
            <MessagesDrawerRight data={dataErrors} title={''} />
            {ResponseComponents}

            <Typography fontSize={25} color="textPrimary" sx={{ fontWeight: 700 }}>Editar Agente</Typography>
            <Breadcrumbs aria-label="breadcrumb" sx={{ padding: 2 }}>
                <Typography color="textPrimary" sx={{ fontWeight: 700 }}>Inteligencia Artificial</Typography>
                <Link color="inherit" to="/manager/plataforma-ia/agentes">
                    Listar Agentes
                </Link>
                <Typography color="textPrimary">Editar Agente</Typography>
            </Breadcrumbs>
            
            <Divider sx={{ marginBottom: 3 }}/>

            <Formik
                initialValues={initialValues}
                enableReinitialize
                validationSchema={validationSchema[0]}
                onSubmit={handleSubmit}
            >
                {({ isSubmitting }) => (
                    <Card sx={{ 
                        mt: 2, 
                        p: 3, 
                        borderRadius: 3, 
                        boxShadow: '0px 12px 24px -4px rgba(107, 76, 255, 0.15)',
                        background: (theme) => `linear-gradient(145deg, ${theme.palette.background.paper} 0%, ${theme.palette.primary.light}08 100%)`,
                        border: 'none'
                    }}>
                        <Form id={formId} autoComplete="off">
                            <AgentesForm formField={formField} servicesOptions={servicesOptions} />

                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4, gap: 2 }}>
                                <Button 
                                    component={Link}
                                    to="/manager/plataforma-ia/agentes"
                                    color="inherit" 
                                    sx={{ borderRadius: 2, textTransform: 'none' }}
                                >
                                    Cancelar
                                </Button>
                                <Button 
                                    type="submit" 
                                    variant="contained" 
                                    disabled={isSubmitting}
                                    sx={{ 
                                        borderRadius: 2, 
                                        textTransform: 'none',
                                        px: 3,
                                        fontWeight: 600,
                                        background: 'linear-gradient(45deg, #6b4cff 30%, #a855f7 90%)',
                                        boxShadow: '0 4px 14px 0 rgba(107, 76, 255, 0.39)',
                                        '&:hover': {
                                            background: 'linear-gradient(45deg, #583be8 30%, #933ce6 90%)',
                                        }
                                    }}
                                >
                                    Guardar Cambios
                                </Button>
                            </Box>
                        </Form>
                    </Card>
                )}
            </Formik>
        </Box>
    );
};
